// ────────────────────────────────────────────────────────────────────
//  SceneInfo.jsx — With auto-navigation to specific metrics
// ──────────────────────────────────────────────────────────────

import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SummaryBlock from './SummaryBlock';
import FixList from './FixList';
import InfoHeader from './InfoHeader';
import ScoreGrid from './ScoreGrid';
import { SCENE_CATEGORIES } from './ScoreCategories';
import { METRIC_KEYS } from '../Constants';
import ExpertPanel from './ExpertPanel';
import PolarChart from './PolarChart';
import ScoreCategorySection from './ScoreCategorySection';
import Tabs from './Tabs';
import './styles/info-panel.css';
import '../grades.css';

// Helper function to find which category contains a specific metric
const findCategoryForMetric = (metricKey, categories) => {
  // Search through all categories to find which one contains the metric
  for (const [categoryName, items] of Object.entries(categories)) {
    const found = items.find(([key, label]) => key === metricKey);
    if (found) {
      return categoryName;
    }
  }
  return null;
};

const Card = ({ title, children }) => (
  <div className="border rounded p-3 mb-3 bg-white shadow-sm">
    <h6 className="fw-semibold mb-2">{title}</h6>
    {children}
  </div>
);
Card.propTypes = { title: PropTypes.node, children: PropTypes.node };

export default function SceneInfo({ data }) {
  const [activeTab, setActiveTab] = useState('designexec');
  const [expandAll, setExpandAll] = useState(false);
  const categoryRefs = useRef({}); // NEW: Refs for each category section
  
  const analysis = data.analysis || {};
  const scores = data.scores || {};
  const experts_critique = data.experts_critique || {};
  const experts_suggestions = data.experts_suggestions || {};
  const selectedMetric = data?.selectedMetric; // NEW: Get the selected metric

  // Auto-navigate when a specific metric is selected
// Auto-navigate when a specific metric is selected
useEffect(() => {
  console.log('SceneInfo useEffect triggered:', { selectedMetric });
  
  if (selectedMetric && selectedMetric !== 'overall') {
    console.log('Processing metric:', selectedMetric);
    
    // Switch to scores tab
    setActiveTab('scores');
    // Expand all sections
    setExpandAll(true);
    
    // Find the actual metric key used in acts
    const metricKey = METRIC_KEYS[selectedMetric]?.scene;
    console.log('Act metric key:', metricKey);
    
    setTimeout(() => {
      // Find the specific score card by its metric key
      const targetCard = document.querySelector(`[data-metric-key="${metricKey}"]`);
      console.log('Found target card:', targetCard);
      
      if (targetCard) {
        targetCard.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' // Center the card in view
        });
        
        // Add a subtle highlight pulse to the specific card
        targetCard.classList.add('metric-target-pulse');
        setTimeout(() => {
          targetCard.classList.remove('metric-target-pulse');
        }, 3000);
      } else {
        console.log('Could not find target card for metric key:', metricKey);
      }
    }, 500);
  }
}, [selectedMetric, data.id]);

  const tabs = [
    { key: 'designexec', label: 'Design & Exec' },
    { key: 'critique', label: 'Critique & Summary' },
    { key: 'scores', label: 'Scores' },
    { key: 'experts', label: 'Experts & Theory' },
    { key: 'text', label: 'Text' }
  ];

  const categoryMeans = useMemo(() => {
    const m = {};
    Object.entries(SCENE_CATEGORIES).forEach(([cat, items]) => {
      const vals = items.map(([k]) => parseFloat(scores[k]?.score)).filter(n => !isNaN(n));
      m[cat] = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
    });
    return m;
  }, [scores]);

  // Render a verdict summary that may be a string or an array of bullets.
  const renderSummary = (s) => {
    if (s == null) return null;
    if (Array.isArray(s)) {
      return (
        <ul className="mb-0">
          {s.map((x, i) => <li key={i}>{typeof x === 'string' ? x : (x?.text ?? JSON.stringify(x))}</li>)}
        </ul>
      );
    }
    return <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{s}</p>;
  };

  const ScoreBadge = ({ label, score }) => (
    <div className="d-flex align-items-center">
      <span className="fw-semibold me-2">{label}</span>
      {score != null
        ? <span className={`badge grade-${Math.max(1, Math.min(10, Math.round(score)))}`}
                style={{ color: '#000', fontSize: '1rem' }}>{Number(score).toFixed(1)}</span>
        : <span className="text-muted">—</span>}
    </div>
  );

  const DesignExecTab = () => {
    const de = data.design_execution || null;

    // Graceful empty state — scenes (or whole scripts) without the analysis.
    // Gated on the blob itself, not on the fill's legacy fallback.
    if (!de) {
      return (
        <div className="tab-content">
          <Card title="Design & Execution">
            <p className="mb-0 text-muted">
              This scene hasn’t been through the design/execution analysis yet.
            </p>
          </Card>
        </div>
      );
    }

    const design = de.design ?? null;
    const execution = de.execution ?? null;
    const bandLabel = de.band
      ? String(de.band).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      : null;

    // One score row: label + colored badge, with the per-dimension explanation
    // underneath (same prose the modal shows next to each score).
    const ScoreRow = ({ label, score, summary }) => (
      <div className="mb-3">
        <ScoreBadge label={label} score={score} />
        {summary && (
          <p className="small text-muted mb-0 mt-1" style={{ whiteSpace: 'pre-wrap' }}>{summary}</p>
        )}
      </div>
    );

    return (
      <div className="tab-content">
        <Card title="Design &amp; Execution">
          {/* verdict band + confidence, and the scene-type ("Analyzed as") chip */}
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            {bandLabel && (
              <span className="badge text-uppercase"
                    style={{ background: '#fff7e6', color: '#9a6700', border: '1px solid #f0d8a8' }}>
                → {bandLabel}
              </span>
            )}
            {de.confidence && <span className="text-muted small">{de.confidence} confidence</span>}
            {de.scene_type && (
              <span className="badge bg-light text-dark border ms-auto">
                Analyzed as: <strong>{de.scene_type}</strong>
              </span>
            )}
          </div>

          {de.reason && <p className="fst-italic mb-3">{de.reason}</p>}

          <ScoreRow label="Design" score={design} summary={de.design_summary} />
          <ScoreRow label="Execution" score={execution} summary={de.execution_summary} />
        </Card>

        {de.scene_gloss && (
          <Card title="What happens">{renderSummary(de.scene_gloss)}</Card>
        )}

        {/* Opens the scene's server-rendered Bootstrap modal. Two contexts:
            - STANDALONE SoS page (AnalysisController::pcr): D&E-only modals
              (#sceneDesignExecModal{n}) are shared into the Inertia root.
            - Analysis-page tab (partials.analysis.view_sunburst_react): those
              modals are NOT rendered there (app.blade.php isn't the layout),
              but the legacy full scene modal (#scenemodals{n}, included by
              view_analysis via scene_modals.blade.php and containing the same
              D&E v2 section) IS — fall back to it rather than injecting a
              second copy of the heavy D&E partial into the page.
            Bootstrap may be window.bootstrap (BS5) or jQuery-based on legacy
            pages — try both, then the custom event as a last resort. */}
        <button
          className="btn btn-sm btn-outline-secondary mt-1"
          onClick={() => {
            const el = data.number != null
              ? (document.getElementById('sceneDesignExecModal' + data.number)
                  || document.getElementById('scenemodals' + data.number))
              : null;
            const bs = (typeof window !== 'undefined') ? window.bootstrap : null;
            if (el && bs?.Modal) {
              bs.Modal.getOrCreateInstance(el).show();
            } else if (el && window.jQuery) {
              window.jQuery(el).modal('show');
            } else {
              window.dispatchEvent(new CustomEvent('scriptsnout:openSceneModal', {
                detail: { sceneId: data.id, number: data.number, tab: 'design-execution' }
              }));
            }
          }}
        >
          See full breakdown →
        </button>
      </div>
    );
  };

  const CritiqueTab = () => (
    <div className="tab-content">
      {data.summary && (
        <Card title="📝 Summary">
          <p className="mb-0">{data.summary}</p>
        </Card>
      )}
  
      {data.critique?.length > 0 && (
        <Card title="🧐 Critique Highlights">
          <ul className="mb-0">
            {data.critique.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
  
  const ScoresTab = () => (
    <div className="tab-content">
      <Card title="At‑a‑Glance Score Shape">
        <PolarChart
          scores={scores}
          axes={[
            { key: 'conceptrating', label: 'Concept' },
            { key: 'charactersrating', label: 'Characters' },
            { key: 'dialoguerating', label: 'Dialogue' },
            { key: 'emotional_impact', label: 'Emotion' },
            { key: 'engagement_score', label: 'Engagement' },
            { key: 'conflict_level', label: 'Conflict' },
            { key: 'originality_score', label: 'Originality' },
            { key: 'unpredictability_score', label: 'Unpredictability' },
            { key: 'pacing_score', label: 'Pacing' },
            { key: 'plotrating', label: 'Plot' },
            { key: 'conceptrating', label: 'Concept' }
          ]}
        />
      </Card>

      <button className="btn btn-sm btn-outline-secondary mb-2" onClick={() => setExpandAll(p => !p)}>
        {expandAll ? <><ChevronUp size={14}/> Collapse All</> : <><ChevronDown size={14}/> Expand All</>}
      </button>

      {Object.entries(SCENE_CATEGORIES).map(([cat, items]) => (
        <div 
          key={cat} 
          className="mb-4"
          ref={el => categoryRefs.current[cat] = el}
        >
          <h5 className="fw-semibold">
            {cat}
            {categoryMeans[cat] && <span className={`badge grade-${Math.round(categoryMeans[cat])} ms-2`} style={{color:'#000'}}>{categoryMeans[cat]}</span>}
          </h5>
          <ScoreCategorySection 
            items={items} 
            scores={scores} 
            expandAll={expandAll}
            highlightedMetric={selectedMetric}
            nodeType="scene"
          />
        </div>
      ))}

      <button className="btn btn-sm btn-outline-secondary mt-2" onClick={() => setExpandAll(p => !p)}>
        {expandAll ? <><ChevronUp size={14}/> Collapse All</> : <><ChevronDown size={14}/> Expand All</>}
      </button>
    </div>
  );

  const ExpertsTab = () => (
    <div className="tab-content">
      {Object.keys(experts_critique).length > 0 && <Card title="Critiques"><ExpertPanel experts={experts_critique} /></Card>}
      {Object.keys(experts_suggestions).length > 0 && <Card title="Suggestions"><ExpertPanel experts={experts_suggestions} /></Card>}
    </div>
  );

  const TextTab = () => (
    <div className="tab-content">
      <Card title="Scene Text">
        <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{data.text}</pre>
      </Card>
    </div>
  );

  const Panel = {
    designexec: DesignExecTab,
    critique: CritiqueTab,
    scores: ScoresTab,
    experts: ExpertsTab,
    text: TextTab
  }[activeTab];

  return (
    <div className="info-panel">
      <InfoHeader title={data.title} id={data.id} execution={data.execution} />
      <div className="info-body">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
        {Panel && <Panel />}
      </div>
    </div>
  );
}

SceneInfo.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
    summary: PropTypes.string,
    critique: PropTypes.array,
    analysis: PropTypes.object,
    scores: PropTypes.object,
    experts_critique: PropTypes.object,
    experts_suggestions: PropTypes.object,
    selectedMetric: PropTypes.string // NEW
  }).isRequired
};